import _ from 'lodash';
import {
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from '@material-ui/core';
import { esES as esLocale } from '@material-ui/core/locale';

import { THEMES } from 'constants/index';

import typography from './typography';
import { PINPEO } from './palette';
import { applyBrandTokens, createBrandTokens } from './brandTokens';
import { softShadows, strongShadows } from './shadows';

const baseConfig = {
  direction: 'ltr',
  typography,
  shape: {
    borderRadius: PINPEO.radius,
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'unset !important',
        borderRadius: PINPEO.radius,
        fontWeight: 600,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlined: {
        borderColor: PINPEO.borderStrong,
      },
    },
    MuiChip: {
      root: {
        // El color por marca (brand.track) se aplica en el override del
        // tema LIGHT dentro de createTheme; aquí solo el neutro base.
        backgroundColor: PINPEO.track,
        fontWeight: 600,
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32,
      },
    },
    MuiTab: {
      root: {
        textTransform: 'unset !important',
        fontWeight: 600,
      },
    },
  },
};

export function createTheme(settings = {}) {
  // Tokens de marca por tenant (settings.colors), derivados en runtime
  // desde el JSON del dominio. Con {} devuelve la marca Pinpeo (fallback).
  const brand = applyBrandTokens(createBrandTokens(settings?.colors));
  const primary = brand.primary;
  // Acento de marca. NO es la superficie oscura del sidebar: esa es
  // `brand.brandDark` (theme.colors.surface). Ver brandTokens.js.
  const secondary = brand.secondary;

  const themeConfigs = [
    {
      name: THEMES.LIGHT,
      overrides: {
        // Cabeceras de tabla estilo propuesta: fondo blanco, label gris
        // uppercase pequeño (aplica a todas las tablas del panel).
        MuiTableCell: {
          root: {
            borderBottom: `1px solid ${PINPEO.divider}`,
          },
          head: {
            padding: '10px 16px !important',
            backgroundColor: `${PINPEO.surface} !important`,
            color: `${PINPEO.textMuted} !important`,
            fontSize: 11.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: `1px solid ${PINPEO.border}`,
            '& .MuiButton': {
              color: `${PINPEO.textMuted} !important`,
            },
          },
        },
        MuiTableRow: {
          root: {
            '&$selected, &$selected:hover': {
              backgroundColor: brand.rowSelected,
            },
          },
          hover: {
            '&:hover': {
              backgroundColor: `${brand.rowHover} !important`,
            },
          },
        },
        MuiChip: {
          root: {
            backgroundColor: brand.track,
          },
        },
        MuiCard: {
          root: {
            borderRadius: PINPEO.radiusCard,
            border: `1px solid ${PINPEO.border}`,
            boxShadow: 'none',
          },
        },
        MuiInputBase: {
          input: {
            '&::placeholder': {
              opacity: 1,
              color: PINPEO.textMuted,
            },
          },
        },
        MuiOutlinedInput: {
          root: {
            borderRadius: PINPEO.radius,
            backgroundColor: PINPEO.surface,
            '& $notchedOutline': {
              borderColor: PINPEO.borderStrong,
            },
            '&:hover $notchedOutline': {
              borderColor: brand.primary,
            },
          },
        },
        // Toolbar de mui-datatables (buscar / descargar / columnas): botones
        // tipo pill con borde y hover verde, estilo propuesta.
        MUIDataTableToolbar: {
          root: {
            borderBottom: `1px solid ${PINPEO.divider}`,
            paddingLeft: 16,
            paddingRight: 12,
          },
          actions: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
          },
          icon: {
            border: `1px solid ${PINPEO.borderStrong}`,
            borderRadius: 9,
            padding: 8,
            backgroundColor: PINPEO.surface,
            color: PINPEO.textSecondary,
            '&:hover': {
              color: brand.primary,
              borderColor: brand.primary,
              backgroundColor: PINPEO.surface,
            },
            '& svg': {
              fontSize: 20,
            },
          },
          iconActive: {
            border: `1px solid ${brand.primary}`,
            borderRadius: 9,
            padding: 8,
            color: brand.primary,
            backgroundColor: brand.primarySoft,
            '&:hover': {
              backgroundColor: brand.primarySoft,
            },
            '& svg': {
              fontSize: 20,
            },
          },
        },
        // Campo de búsqueda del toolbar: input redondeado con borde en lugar
        // del subrayado por defecto.
        MUIDataTableSearch: {
          main: {
            alignItems: 'center',
          },
          searchIcon: {
            color: PINPEO.textMuted,
            marginTop: 0,
            marginRight: 8,
          },
          searchText: {
            flex: '0.6 0',
            '& .MuiInputBase-root': {
              border: `1px solid ${PINPEO.borderStrong}`,
              borderRadius: 9,
              backgroundColor: PINPEO.surface,
              padding: '4px 12px',
              fontSize: 13.5,
            },
            '& .MuiInputBase-root.Mui-focused': {
              borderColor: brand.primary,
            },
            '& .MuiInput-underline:before, & .MuiInput-underline:after': {
              display: 'none',
            },
          },
          clearIcon: {
            color: PINPEO.textMuted,
            '&:hover': {
              color: brand.primary,
            },
          },
        },
        MUIDataTableBodyRow: {
          root: {
            '&:nth-child(even)': {
              backgroundColor: brand.rowStripe,
            },
            '&:hover': {
              backgroundColor: `${brand.rowHover} !important`,
            },
          },
        },
        MUIDataTableHeadCell: {
          contentWrapper: {
            width: '100% !important',
            display: 'block',
            alignItems: 'center',
          },
          dragCursor: {
            width: '100%',
          },
          sortAction: {
            alignItems: 'center',
            cursor: 'pointer',
            display: 'contents',
            justifyContent: 'left',
            width: '100% !important',
          },
          toolButton: {
            width: '100%',
            marginLeft: '0',
            marginRight: '0',
          },
        },
        MuiButton: {
          label: {
            '& span': {
              textAlign: 'left !important',
            },
          },
        },
      },
      palette: {
        type: 'light',
        action: {
          active: PINPEO.textSecondary,
        },
        background: {
          default: PINPEO.background,
          dark: PINPEO.background,
          paper: PINPEO.surface,
        },
        divider: PINPEO.border,
        primary: {
          main: primary,
          dark: brand.primaryHover,
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: secondary,
          contrastText: '#FFFFFF',
        },
        text: {
          primary: PINPEO.text,
          secondary: PINPEO.textSecondary,
        },
      },
      shadows: softShadows,
    },
    {
      name: THEMES.ONE_DARK,
      palette: {
        type: 'dark',
        action: {
          active: 'rgba(255, 255, 255, 0.54)',
          hover: 'rgba(255, 255, 255, 0.04)',
          selected: 'rgba(255, 255, 255, 0.08)',
          disabled: 'rgba(255, 255, 255, 0.26)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
          focus: 'rgba(255, 255, 255, 0.12)',
        },
        background: {
          default: '#282C34',
          dark: '#1c2025',
          paper: '#282C34',
        },
        primary: {
          main: '#8a85ff',
        },
        secondary: {
          main: '#8a85ff',
        },
        text: {
          primary: '#e6e5e8',
          secondary: '#adb0bb',
        },
      },
      shadows: strongShadows,
    },
    {
      name: THEMES.UNICORN,
      palette: {
        type: 'dark',
        action: {
          active: 'rgba(255, 255, 255, 0.54)',
          hover: 'rgba(255, 255, 255, 0.04)',
          selected: 'rgba(255, 255, 255, 0.08)',
          disabled: 'rgba(255, 255, 255, 0.26)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
          focus: 'rgba(255, 255, 255, 0.12)',
        },
        background: {
          default: '#2a2d3d',
          dark: '#222431',
          paper: '#2a2d3d',
        },
        primary: {
          main: '#a67dff',
        },
        secondary: {
          main: '#a67dff',
        },
        text: {
          primary: '#f6f5f8',
          secondary: '#9699a4',
        },
      },
      shadows: strongShadows,
    },
  ];

  let themeConfig = themeConfigs.find((theme) => theme.name === settings.theme);

  if (!themeConfig) {
    [themeConfig] = themeConfigs;
  }

  let theme = createMuiTheme(
    _.merge(
      {},
      baseConfig,
      themeConfig,
      {
        direction: settings.direction,
        // Tokens de marca derivados, expuestos como theme.brand para
        // consumo vía makeStyles((theme) => ...) o useTheme().
        brand,
      },
    ),
    esLocale,
  );

  if (settings.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
}
